import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { SvgSpacesDataStatus } from '../../../gen-src/icons/SpacesDataStatus';
import { SvgSpacesDataStatusActive } from '../../../gen-src/icons/SpacesDataStatusActive';
import { SvgSpacesMediumDs } from '../../../gen-src/icons/SpacesMediumDs';
import { SvgSpacesMediumExternalServer } from '../../../gen-src/icons/SpacesMediumExternalServer';
import { SvgSpacesMediumFile } from "../../../gen-src/icons/SpacesMediumFile";
import { SvgSpacesMediumFlow } from '../../../gen-src/icons/SpacesMediumFlow';
import { SvgSpacesMediumTable } from '../../../gen-src/icons/SpacesMediumTable';
import { SvgWarningHighSeverity } from '../../../gen-src/icons/WarningHighSeverity';
import { SvgWarningHighSeverityActive } from '../../../gen-src/icons/WarningHighSeverityActive';
import { TableauUser, UserLinkDisplayComponent, UserLinkProps, UserTextDisplayComponent } from '../UserDisplayComponent';
import { SingleLineLinkWithIcon, SingleLineTextWithIcon } from '../ViewerPaneLayoutComponents';
import { DataQualityType } from '../dataQualityWarnings/DataQualityType';
import { FeatureFlagsContext } from '../featureFlags/FeatureFlagsContext';
import { IndicatorDetailAssetStyle, IndicatorDetailAuthorStyle, IndicatorDetailDateStyle, IndicatorDetailMessageStyle, IndicatorDetailPermissionRequired, IndicatorStatusChipIconClass } from '../style/DataQualityIndicatorStyles';
import { FormattedMarkupWrapperInTooltip } from '../style/MarkupStyles';
import { DataQualityDetailPanelWidth } from '../style/StyleConstants';
import { linkClass } from '../style/VizPortalStyles';
import { interpolateString } from '../utils/StringInterpolation';
import { assertUnreachable } from '../utils/TypescriptHelpers';

export type RenderIndicatorProps = {
  readonly kind: DataQualityIndicatorKind;
  readonly author: TableauUser;
  readonly localizedDate: string;
  readonly type: DataQualityType;
  readonly message: string | null;
  readonly newTab?: boolean; // TODO: maybe not used
  readonly linksDisabled?: boolean; // TOOD: maybe not used
};

export type DataQualityIndicatorAssetType = 'CloudFile'
  | 'DatabaseServer'
  | 'DatabaseTable'
  | 'File'
  | 'Flow'
  | 'PublishedDatasource'
  | 'WebDataConnector'
  | 'VirtualConnectionTable'
  | 'VirtualConnection'
  | null;

export type IndicatorSubjectArea =
  DataQualityIndicatorAssetType
  | 'View'
  | 'Metric'
  | 'Workbook'
  | 'EmbeddedDatasource'
  | null;

export type RenderIndicatorAssetProps = {
  readonly subjectArea: IndicatorSubjectArea;
  readonly assetName: string | null;
  readonly assetType: DataQualityIndicatorAssetType | null;
  readonly assetUrl: string | null;
  readonly indicators: RenderIndicatorProps[];
};

export type RenderIndicatorsProps = {
  readonly newTab?: boolean;
  readonly linksDisabled?: boolean;
  readonly upstream: ReadonlyArray<RenderIndicatorAssetProps>;
} & RenderIndicatorAssetProps;

export type DataQualityCertificationKind = 'certification';
export type DataQualityWarningKind = 'standard-warning' | 'severe-warning';
export type DataQualityIndicatorKind = DataQualityCertificationKind | DataQualityWarningKind;

export const DataQualityDetailChipId = 'data-quality-detail-chip-id';
export const DataQualityDetailMessageId = 'data-quality-detail-message-id';


function assetIconForType(typename: IndicatorSubjectArea): JSX.Element | null {
  switch (typename) {
    case 'CloudFile':
    case 'File':
      return <SvgSpacesMediumFile />;
    case 'DatabaseServer':
    case 'WebDataConnector':
      return <SvgSpacesMediumExternalServer />;
    case 'DatabaseTable':
      return <SvgSpacesMediumTable />;
    case 'Flow':
      return <SvgSpacesMediumFlow />;
    case 'PublishedDatasource':
      return <SvgSpacesMediumDs />;
    default:
      return null;
  }
}

function getPermissionRequiredMessage(): JSX.Element {
  const name = Messages.PermissionRequired();
  return <span key={'asset-' + name} style={IndicatorDetailPermissionRequired}>{name}</span>;
}

export type InterpolateAssetProps = {
  assetName: string | null;
  assetType: DataQualityIndicatorAssetType | null;
  assetUrl: string | null;
  children?: JSX.Element;
};

export function DataQualityDetailAsset(props: InterpolateAssetProps): JSX.Element | null {
  if (props.assetName === null) { return getPermissionRequiredMessage(); }
  const iconComponent = assetIconForType(props.assetType);

  return (
    <IndicatorDetailAssetStyle>
      {
        props.assetUrl ?
          <SingleLineLinkWithIcon
            linkText={props.assetName || null}
            linkHref={props.assetUrl}
            iconComponent={iconComponent}
            iconSize={'small'}
          /> :
          <SingleLineTextWithIcon
            text={props.assetName || null}
            iconComponent={iconComponent}
            iconSize={'small'}
          />
      }
    </IndicatorDetailAssetStyle>
  )
}

function DataQualityAuthorLink(props: UserLinkProps) {
  return <a href={props.url} target='_blank' className={linkClass} title={props.name} rel="noreferrer">{props.name}</a>;
}

export type DataQualityDetailAuthorElementProps = {
  readonly kind: DataQualityIndicatorKind;
  readonly linksDisabled?: boolean;
  readonly author: TableauUser;
};

export function DataQualityDetailAuthorElement(props: DataQualityDetailAuthorElementProps): JSX.Element {
  return (
    <IndicatorDetailAuthorStyle>
      <DataQualityDetailAuthorElementInner {...props} />
    </IndicatorDetailAuthorStyle>
  )
}

export function DataQualityDetailAuthorElementInner(props: {
  readonly kind: DataQualityIndicatorKind;
  readonly linksDisabled?: boolean;
  readonly author: TableauUser;
}): JSX.Element {
  const userElement = props.linksDisabled
    ? <UserTextDisplayComponent user={props.author} />
    : <UserLinkDisplayComponent user={props.author} linkComponent={DataQualityAuthorLink} />;
  const name = '[AUTHOR_NAME_TOKEN]';
  const span = <span key='author' data-tb-test-id={'Data-Quality-Detail-Author'}>{userElement}</span>;

  switch (props.kind) {
    case 'severe-warning':
    case 'standard-warning':
      return (
        <>
          {
            interpolateString(Messages.DataQualityAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })
          }
        </>
      );
    case 'certification':
      return (
        <>
          {
            interpolateString(Messages.DataQualityCertificationAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })
          }
        </>
      );
    default:
      assertUnreachable(props.kind);
      return <></>;
  }
}

export function DataQualityDetailDateElement(props: {
  readonly localizedDate: string;
}): JSX.Element {
  return (
    <IndicatorDetailDateStyle>
      {props.localizedDate}
    </IndicatorDetailDateStyle>
  )
}

function MarkupOrSplitString(props: {
  readonly message: string | null;
  readonly linksDisabled?: boolean;
}): JSX.Element {
  const { markupEditingEnabled } = React.useContext(FeatureFlagsContext);

  const { message, linksDisabled } = props;
  const messageOrBlank = message || '';

  return (markupEditingEnabled ?
    <FormattedMarkupWrapperInTooltip markup={messageOrBlank} linksDisabled={linksDisabled} /> :
    <>{messageOrBlank.split('\n').map(l => <p key={l}>{l}</p>)}</>
  );
}

export function DataQualityDetailMessageElement(props: {
  readonly assetName: string | null;
  readonly message: string | null;
  readonly linksDisabled?: boolean;
}): JSX.Element {
  if (props.assetName === null) {
    return (
      <IndicatorDetailMessageStyle id={DataQualityDetailMessageId}>
        {Messages.PermissionRequiredMessage()}
      </IndicatorDetailMessageStyle>
    );
  }

  return (
    <IndicatorDetailMessageStyle>
      <MarkupOrSplitString {...props} />
    </IndicatorDetailMessageStyle>
  );
}

export type DataQualityDetailPanelSide = 'left' | 'right';

export function getTooltipAnchorClientRect(el?: HTMLElement): ClientRect {
  if (el) {
    const clientRect = el.getBoundingClientRect();
    return {
      left: clientRect.left - DataQualityDetailPanelWidth,
      right: clientRect.right,
      top: clientRect.top,
      bottom: clientRect.bottom,
    } as ClientRect;
  }

  return {} as ClientRect;
}

function DataStatusAnchored(props: { hover: boolean, isSevere: boolean }): JSX.Element {
  if (props.hover) {
    return props.isSevere ?
      <SvgWarningHighSeverityActive className={IndicatorStatusChipIconClass} /> :
      <SvgSpacesDataStatusActive className={IndicatorStatusChipIconClass} />;
  } else {
    return props.isSevere ?
      <SvgWarningHighSeverity className={IndicatorStatusChipIconClass} /> :
      <SvgSpacesDataStatus className={IndicatorStatusChipIconClass} />;
  }
}

export function DataStatusIcon(props: { hover: boolean, isSevere: boolean }): JSX.Element {
  return (
    <DataStatusAnchored hover={props.hover} isSevere={props.isSevere} />
  );
}
